/**
 * 2026-02-18 Round D2 — Auto-OCR everywhere helper.
 *
 * Wraps the two-step backend flow used by ANY upload surface that wants
 * the file to also land in `/ocr/invoices` for review:
 *
 *   1. POST /api/uploads            (raw bytes → upload_id, w/ SHA-256 dedup)
 *   2. POST /api/ocr/invoice/from-upload {upload_id, customer_id?, ...}
 *
 * The first step is intentionally redundant with whatever
 * context-specific upload the caller is doing (profile-doc, project-doc,
 * etc.) — both paths end up sharing the same `uploads` row when content
 * is byte-identical (SHA-256 dedup → next-call returns the same id).
 *
 * Trust contract:
 *   - NEVER raises. Failure ⇒ console.warn + silent. The user's original
 *     upload (profile-doc / project-doc) is the source of truth; this
 *     auto-OCR is purely additive.
 *   - Returns the created OCR stub job, or `null` on skip/failure.
 *   - Non-image / non-PDF files are skipped silently.
 *
 * Usage:
 *     import { kickOffAutoOcr } from "../../lib/autoOcr";
 *     await kickOffAutoOcr(file, {
 *       customer_id: cid, project_id: pid, source: "profile_doc",
 *     });
 */
import api from "./api";

const OCR_MIME_PREFIX = ["image/", "application/pdf"];

function isOcrCandidate(file) {
  if (!file || !file.type) return false;
  const t = file.type.toLowerCase();
  return OCR_MIME_PREFIX.some((p) => t.startsWith(p));
}

export async function kickOffAutoOcr(file, ctx = {}) {
  if (!isOcrCandidate(file)) return null;
  try {
    // 1) Upload bytes. Use timeout:0 so flaky mobile networks never abort.
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source", ctx.source || "auto_ocr");
    const up = await api.post(`/uploads`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
    });
    const upload_id = up.data?.id;
    if (!upload_id) return null;
    // 2) Queue OCR job.
    const r = await api.post(`/ocr/invoice/from-upload`, {
      upload_id,
      customer_id: ctx.customer_id || null,
      site_id: ctx.site_id || null,
      project_id: ctx.project_id || null,
      notes: ctx.notes || `Auto-OCR'd from ${ctx.source || "upload"} · ${file.name}`,
    });
    return r.data || null;
  } catch (err) {
    // Trust contract — never raise. Visible in console for devs.
    // eslint-disable-next-line no-console
    console.warn("autoOcr failed (non-blocking)", err);
    return null;
  }
}

/**
 * Fire-and-forget batch — fans `kickOffAutoOcr` across multiple files in
 * parallel. Returns the count of jobs actually queued (skips non-image
 * / non-PDF files).
 */
export async function kickOffAutoOcrBatch(files, ctx = {}) {
  const list = Array.from(files || []).filter(isOcrCandidate);
  if (list.length === 0) return 0;
  const results = await Promise.all(list.map((f) => kickOffAutoOcr(f, ctx)));
  return results.filter(Boolean).length;
}
